import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    BookOpenIcon,
    LightBulbIcon,
    AcademicCapIcon,
    ArrowRightIcon,
    CheckCircleIcon,
    ShieldCheckIcon,
    DocumentTextIcon,
    PlayIcon,
    XMarkIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import i18n from '../i18n';

/* ─── WIKI CONTENT (FR / EN) ─────────────────────────────── */
const WIKI = {
    fr: [
        {
            id: 'h2-offre',
            title: "L'Offre Maroc pour l'Hydrogène Vert",
            icon: LightBulbIcon,
            badge: 'Loi 82-21',
            badgeColor: 'amber',
            desc: 'Comprendre comment le registre gère la séparation 800/200 pour les projets en autoproduction.',
            steps: [
                { title: 'Dépôt de la lecture compteur', body: "Le Gestionnaire de Réseau (ONEE) saisit la lecture totale mesurée sur site pour le projet d'autoproduction." },
                { title: 'Validation du volume total', body: "Le moteur de calcul valide que la somme (Self-Consumption + Grid-Injected) est strictement égale au total mesuré. Toute incohérence est bloquée à la source." },
                { title: 'Génération de deux certificats distincts', body: "Si les données sont valides, le système crée automatiquement deux GOs : un certificat « Consommation Propre » (badge Indigo) et un certificat « Injection Réseau » (badge Bleu)." },
                { title: 'Approbation régalienne MTEDD', body: "Même après la vérification ONEE, aucun certificat n'est créé sans la validation finale du MTEDD. Cela garantit une gouvernance souveraine à double signature." },
            ],
        },
        {
            id: 'direct-line',
            title: 'Lignes Directes & Loi 13-09',
            icon: ShieldCheckIcon,
            badge: 'Loi 13-09',
            badgeColor: 'indigo',
            desc: 'Procédures de vérification pour les projets hors réseau national (VVB obligatoire).',
            steps: [
                { title: 'Désignation du VVB', body: "L'actif en ligne directe doit être assigné à un auditeur indépendant (VVB) accrédité ISO 14065 pour préserver la confidentialité industrielle et exclure ONEE du périmètre." },
                { title: 'Dépôt de preuve par le VVB', body: "Le VVB téléverse la preuve de lecture (rapport d'audit signé) directement dans le portail sécurisé. Le système enregistre le sceau de vérification : « Vérifié par : Auditeur Indépendant »." },
                { title: 'Soumission au file d\'attente MTEDD', body: "La demande de certificat entre en file d'attente pour examen souverain, tagged automatiquement comme issu d'une ligne directe (is_direct_line = True)." },
                { title: 'Minting par la Ministère', body: "Le MTEDD valide et « Mint GO » — le certificat est créé avec son empreinte SHA-256 et son code QR de vérification publique." },
            ],
        },
        {
            id: 'red-ii',
            title: "Conformité Export RED II (Europe)",
            icon: AcademicCapIcon,
            badge: 'RED II',
            badgeColor: 'emerald',
            desc: "Règles critiques du registre pour la validité transactionnelle et l'exportation vers l'Union Européenne.",
            steps: [
                { title: 'Verrouillage des transferts à 12 mois', body: "Toute transaction (transfert ou vente) est automatiquement bloquée si le certificat a plus de 12 mois après la fin de la période de production. Cette règle est appliquée au niveau du moteur de transfert." },
                { title: "Divulgation jusqu'à 18 mois", body: "La « divulgation » (annulation finale) reste possible jusqu'à 18 mois. L'attestation d'annulation générée est bilinguiste (FR/EN) et constitue la preuve légale pour le reporting carbone européen." },
                { title: 'Empreinte cryptographique SHA-256', body: "Chaque certificat porte un hash SHA-256 unique généré au moment du minting, garantissant l'immuabilité des données et la détection de toute falsification." },
                { title: 'Portail de vérification publique', body: "Le QR code intégré dans chaque PDF offre une vérification instantanée sur le portail public sans nécessiter de compte, accessible par n'importe quel auditeur international." },
            ],
        },
    ],
    en: [
        {
            id: 'h2-offre',
            title: "Morocco's Green Hydrogen Offer",
            icon: LightBulbIcon,
            badge: 'Law 82-21',
            badgeColor: 'amber',
            desc: 'Understand how the registry handles the 800/200 split for autoproduction projects.',
            steps: [
                { title: 'Meter Reading Submission', body: "The Grid Operator (ONEE) submits the total metered output for the autoproduction project directly into the Metering Portal." },
                { title: 'Volume Balance Validation', body: "The calculation engine validates that Self-Consumption + Grid-Injected are exactly equal to the total metered volume. Any mismatch is rejected at source." },
                { title: 'Two Distinct Certificates Generated', body: "If data is valid, the system automatically creates two GOs: a 'Self-Consumption' certificate (Indigo badge) and a 'Grid-Injected' certificate (Blue badge)." },
                { title: 'Sovereign MTEDD Approval', body: "Even after ONEE verification, no certificate exists without the final MTEDD validation. This ensures sovereign dual-signature governance." },
            ],
        },
        {
            id: 'direct-line',
            title: 'Direct Lines & Law 13-09',
            icon: ShieldCheckIcon,
            badge: 'Law 13-09',
            badgeColor: 'indigo',
            desc: 'Verification procedures for off-ONEE projects requiring an Independent Auditor (VVB).',
            steps: [
                { title: 'VVB Designation', body: "A direct-line asset must be assigned to an ISO 14065 accredited Independent Auditor (VVB) to preserve industrial confidentiality and exclude ONEE from the audit perimeter." },
                { title: 'VVB Evidence Upload', body: "The VVB uploads the signed audit evidence directly into the secure portal. The system automatically stamps the certificate: 'Verified by: Independent Auditor (VVB)'." },
                { title: 'MTEDD Queue Submission', body: "The certificate request enters the sovereign approval queue, auto-tagged as a direct-line asset (is_direct_line = True)." },
                { title: 'Ministry Minting', body: "The MTEDD reviews and clicks 'Mint GO' — the certificate is created with its SHA-256 fingerprint and a public QR verification code." },
            ],
        },
        {
            id: 'red-ii',
            title: 'RED II European Export Compliance',
            icon: AcademicCapIcon,
            badge: 'RED II',
            badgeColor: 'emerald',
            desc: 'Critical registry rules for transactional validity and export to the European Union.',
            steps: [
                { title: '12-Month Transfer Lock', body: "All transactions (transfers or sales) are automatically blocked if the certificate is older than 12 months past the production period end. This rule is enforced at the transfer engine level." },
                { title: 'Disclosure up to 18 Months', body: "Final 'disclosure' (cancellation) remains possible up to 18 months. The bilingual (FR/EN) cancellation statement generated is bulletproof legal evidence for European carbon reporting." },
                { title: 'SHA-256 Cryptographic Fingerprint', body: "Every certificate carries a unique SHA-256 hash generated at minting, guaranteeing data immutability and tamper detection." },
                { title: 'Public Verification Portal', body: "The QR code embedded in each PDF provides instant verification on the public portal without requiring an account, accessible to any international auditor." },
            ],
        },
    ],
};

/* ─── TOUR STEPS ────────────────────────────────────────── */
const buildTourSteps = (lang) => [
    {
        target: '#kh-header',
        title: lang === 'fr' ? 'Bienvenue au Centre de Connaissances' : 'Welcome to the Knowledge Hub',
        body: lang === 'fr'
            ? 'Ici, vous trouverez toutes les ressources pour naviguer dans le cadre réglementaire marocain et les normes EECS.'
            : 'Here you will find all resources to navigate the Moroccan regulatory framework and EECS standards.',
        placement: 'bottom',
    },
    {
        target: '#kh-nav',
        title: lang === 'fr' ? 'Navigation Thématique' : 'Thematic Navigation',
        body: lang === 'fr'
            ? 'Sélectionnez un sujet pour afficher les étapes détaillées de conformité dans le panneau principal.'
            : 'Select a topic to display detailed compliance steps in the main panel.',
        placement: 'right',
    },
    {
        target: '#kh-content',
        title: lang === 'fr' ? 'Guide Pas à Pas' : 'Step-by-Step Guide',
        body: lang === 'fr'
            ? 'Chaque guide est un flux de travail complet, approuvé par la cellule réglementaire du MTEDD.'
            : 'Each guide is a complete workflow, approved by the MTEDD regulatory cell.',
        placement: 'left',
    },
];

/* ─── TOUR OVERLAY ──────────────────────────────────────── */
function TourOverlay({ steps, onClose }) {
    const [idx, setIdx] = useState(0);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const popRef = useRef(null);

    const current = steps[idx];

    const reposition = useCallback(() => {
        const el = document.querySelector(current.target);
        if (!el) return;
        const r = el.getBoundingClientRect();
        const pop = popRef.current;
        if (!pop) return;
        const ph = pop.offsetHeight || 200;
        const pw = pop.offsetWidth || 320;

        let top = 0, left = 0;
        const margin = 16;
        switch (current.placement) {
            case 'bottom': top = r.bottom + margin; left = r.left + r.width / 2 - pw / 2; break;
            case 'right': top = r.top + r.height / 2 - ph / 2; left = r.right + margin; break;
            case 'left': top = r.top + r.height / 2 - ph / 2; left = r.left - pw - margin; break;
            default: top = r.top - ph - margin; left = r.left + r.width / 2 - pw / 2;
        }
        // clamp to viewport
        top = Math.max(8, Math.min(top, window.innerHeight - ph - 8));
        left = Math.max(8, Math.min(left, window.innerWidth - pw - 8));
        setPos({ top, left });

        // highlight
        el.classList.add('kh-tour-highlight');
        return () => el.classList.remove('kh-tour-highlight');
    }, [current]);

    useEffect(() => {
        const cleanup = reposition();
        window.addEventListener('resize', reposition);
        return () => { window.removeEventListener('resize', reposition); cleanup?.(); };
    }, [reposition]);

    return (
        <>
            {/* Scrim */}
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[900]" onClick={onClose} />
            {/* Popover */}
            <div
                ref={popRef}
                className="fixed z-[901] w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden animate-fade-in"
                style={{ top: pos.top, left: pos.left }}
            >
                {/* Progress bar */}
                <div className="h-1 bg-gray-100">
                    <div
                        className="h-1 bg-[#155e75] transition-all duration-500"
                        style={{ width: `${((idx + 1) / steps.length) * 100}%` }}
                    />
                </div>
                <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-[9px] font-black text-[#155e75] uppercase tracking-widest">
                            {idx + 1} / {steps.length}
                        </span>
                        <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 transition-colors">
                            <XMarkIcon className="w-4 h-4 text-slate-400" />
                        </button>
                    </div>
                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-2">{current.title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed font-medium">{current.body}</p>
                </div>
                <div className="px-6 pb-5 flex justify-between items-center">
                    <button
                        onClick={() => setIdx(i => i - 1)}
                        disabled={idx === 0}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-50 text-slate-400 text-[10px] font-black uppercase hover:bg-slate-100 disabled:opacity-30 transition-all"
                    >
                        <ChevronLeftIcon className="w-3.5 h-3.5" /> Prev
                    </button>
                    {idx < steps.length - 1 ? (
                        <button
                            onClick={() => setIdx(i => i + 1)}
                            className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#155e75] text-white text-[10px] font-black uppercase hover:bg-[#0e4e61] transition-all shadow-md"
                        >
                            Next <ChevronRightIcon className="w-3.5 h-3.5" />
                        </button>
                    ) : (
                        <button
                            onClick={onClose}
                            className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-emerald-600 text-white text-[10px] font-black uppercase hover:bg-emerald-700 transition-all shadow-md"
                        >
                            <CheckCircleIcon className="w-3.5 h-3.5" /> Done
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}

/* ─── BADGE COLORS ──────────────────────────────────────── */
const BADGE = {
    amber: 'bg-amber-50   border-amber-200   text-amber-700',
    indigo: 'bg-indigo-50  border-indigo-200  text-indigo-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
};

/* ─── MAIN PAGE ─────────────────────────────────────────── */
export default function KnowledgeHub() {
    const lang = (i18n.language || 'en').startsWith('fr') ? 'fr' : 'en';
    const topics = WIKI[lang];

    const [activeTab, setActiveTab] = useState(topics[0].id);
    const [tourActive, setTourActive] = useState(false);
    const tourSteps = buildTourSteps(lang);

    const activeTopic = topics.find(t => t.id === activeTab) || topics[0];

    return (
        <div className="max-w-6xl mx-auto py-10 space-y-10 animate-fade-in">

            {/* Tour overlay */}
            {tourActive && (
                <TourOverlay steps={tourSteps} onClose={() => setTourActive(false)} />
            )}

            {/* ── Header ── */}
            <div id="kh-header" className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-gray-100 pb-10">
                <div className="flex gap-4 items-center">
                    <div className="w-16 h-16 bg-[#155e75] text-white rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                        <BookOpenIcon className="w-9 h-9" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-gray-900 tracking-tight uppercase">Knowledge Hub</h1>
                        <p className="text-[11px] text-gray-400 font-black uppercase tracking-[0.2em] mt-1">
                            {lang === 'fr' ? 'Portail de Conformité Souveraine — MAP GO' : 'Sovereign Compliance Portal — MAP GO'}
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => setTourActive(true)}
                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-xl font-black text-[11px] uppercase tracking-widest shadow-xl shadow-indigo-600/20 transition-all group flex-shrink-0"
                >
                    <PlayIcon className="w-4 h-4 transition-transform group-hover:scale-125" />
                    {lang === 'fr' ? 'Démarrer le Guide Interactif' : 'Start Guided Tour'}
                </button>
            </div>

            {/* ── Body ── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                {/* Sidebar nav */}
                <nav id="kh-nav" className="lg:col-span-4 space-y-3">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 mb-4">
                        {lang === 'fr' ? 'Thèmes Principaux' : 'Main Topics'}
                    </p>
                    {topics.map(topic => {
                        const isActive = topic.id === activeTab;
                        return (
                            <button
                                key={topic.id}
                                onClick={() => setActiveTab(topic.id)}
                                className={`w-full text-left p-5 rounded-2xl border transition-all duration-200 flex items-start gap-4 ${isActive
                                    ? 'bg-white border-[#155e75]/50 shadow-lg translate-x-1'
                                    : 'bg-gray-50/60  border-gray-100 hover:bg-white hover:border-gray-200 hover:shadow-sm'
                                    }`}
                            >
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-[#155e75] text-white' : 'bg-gray-100 text-gray-400'}`}>
                                    <topic.icon className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`inline-block px-2 py-0.5 rounded-full text-[8px] font-black uppercase border ${BADGE[topic.badgeColor]}`}>
                                            {topic.badge}
                                        </span>
                                    </div>
                                    <h4 className={`text-xs font-black uppercase tracking-tight leading-tight ${isActive ? 'text-gray-900' : 'text-gray-600'}`}>
                                        {topic.title}
                                    </h4>
                                    <p className="text-[10px] text-gray-400 mt-1 font-medium leading-snug line-clamp-2">{topic.desc}</p>
                                </div>
                            </button>
                        );
                    })}
                </nav>

                {/* Content panel */}
                <div id="kh-content" className="lg:col-span-8 bg-white border border-gray-100 rounded-3xl shadow-sm p-8 sm:p-12 relative overflow-hidden">

                    {/* Decorative watermark */}
                    <div className="absolute top-0 right-0 p-10 opacity-[0.04] pointer-events-none select-none">
                        <AcademicCapIcon className="w-72 h-72 text-[#155e75]" />
                    </div>

                    <div key={activeTopic.id} className="animate-fade-in space-y-10 relative z-10">

                        {/* Topic header */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] font-black uppercase border ${BADGE[activeTopic.badgeColor]}`}>
                                    <DocumentTextIcon className="w-3 h-3" />
                                    {activeTopic.badge}
                                </span>
                                <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">
                                    {lang === 'fr' ? 'GUIDE RÉGLEMENTAIRE' : 'REGULATORY GUIDE'}
                                </span>
                            </div>
                            <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight leading-tight">
                                {activeTopic.title}
                            </h2>
                            <p className="text-sm text-gray-500 font-medium leading-relaxed max-w-xl">
                                {activeTopic.desc}
                            </p>
                        </div>

                        {/* Step-by-step */}
                        <div className="space-y-0">
                            {activeTopic.steps.map((step, idx) => (
                                <div key={idx} className="flex gap-5 group">
                                    {/* Step number + connector */}
                                    <div className="flex flex-col items-center">
                                        <div className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-[#155e75] font-black text-sm group-hover:bg-[#155e75] group-hover:text-white group-hover:border-[#155e75] transition-all shadow-sm flex-shrink-0">
                                            {idx + 1}
                                        </div>
                                        {idx < activeTopic.steps.length - 1 && (
                                            <div className="w-px flex-1 bg-gray-100 my-2 min-h-[20px]" />
                                        )}
                                    </div>
                                    {/* Step content */}
                                    <div className={`pt-1.5 pb-8 ${idx === activeTopic.steps.length - 1 ? 'pb-2' : ''}`}>
                                        <h5 className="text-[11px] font-black text-gray-900 uppercase tracking-wide mb-1.5 group-hover:text-[#155e75] transition-colors">
                                            {step.title}
                                        </h5>
                                        <p className="text-sm text-gray-500 font-medium leading-relaxed">{step.body}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="pt-8 border-t border-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-2.5">
                                <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                    {lang === 'fr' ? 'Vérifié par la Cellule Réglementaire MTEDD' : 'Verified by MTEDD Regulatory Cell'}
                                </p>
                            </div>
                            <button className="flex items-center gap-2 text-[10px] font-black text-[#155e75] uppercase tracking-widest hover:gap-4 transition-all group">
                                {lang === 'fr' ? 'Voir Décret 2.24.761' : 'View Decree 2.24.761'}
                                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
